import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ClipboardList, Send, Trash2 } from 'lucide-react'

import { STOCKTAKE_KIND_LABELS } from './-stocktake-kinds'
import type { StocktakeStatus } from '@/lib/api/stocktake-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import {
    ScanLotCodeButton,
    parseAnyLotIdFromQR,
} from '@/components/lots/scan-lot-code-button'
import { PageBackButton } from '@/components/common/PageBackButton'
import { StocktakeApproveDialog } from '@/components/stocktake/stocktake-approve-dialog'
import { StocktakeLineCard } from '@/components/stocktake/stocktake-line-card'
import { StocktakeLineRow } from '@/components/stocktake/stocktake-line-row'
import { StocktakeSubmitDialog } from '@/components/stocktake/stocktake-submit-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useCancelStocktake,
    useStocktakeSession,
    useUpdateStocktakeLine,
} from '@/hooks/use-stocktake-query'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/_authed/_sidebar/stocktake/$id')({
    component: StocktakeDetailPage,
})

const KIND_LABELS = STOCKTAKE_KIND_LABELS

const STATUS_LABELS: Record<StocktakeStatus, () => string> = {
    draft: () => m.stocktake_status_draft(),
    counting: () => m.stocktake_status_counting(),
    pending_approval: () => m.stocktake_status_pending_approval(),
    approved: () => m.stocktake_status_approved(),
    cancelled: () => m.stocktake_status_cancelled(),
}

const STATUS_COLORS: Record<StocktakeStatus, string> = {
    draft: 'bg-slate-100 text-slate-700',
    counting: 'bg-blue-100 text-blue-700',
    pending_approval: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-700',
}

type LineFilter =
    'all' | 'uncounted' | 'counted' | 'variance' | 'activity' | 'no_activity'

export function StocktakeDetailPage() {
    const { id } = Route.useParams()
    const navigate = useNavigate()
    const { data: session, isLoading } = useStocktakeSession(id)
    const updateLine = useUpdateStocktakeLine(id)
    const cancel = useCancelStocktake()
    const cancelWorkflow = useWorkflowTracking('stocktake_cancel', {
        operation: 'cancel',
        attemptKey: id,
        autoStart: false,
    })

    const [filter, setFilter] = useState<LineFilter>('all')
    const [search, setSearch] = useState('')
    const [submitOpen, setSubmitOpen] = useState(false)
    const [approveOpen, setApproveOpen] = useState(false)

    const lines = session?.lines ?? []

    const filteredLines = useMemo(() => {
        return lines.filter((l) => {
            if (
                search &&
                !l.lot_code.toLowerCase().includes(search.toLowerCase()) &&
                !l.warehouse_name
                    .toLowerCase()
                    .includes(search.toLowerCase()) &&
                !(l.material_or_product_name ?? '')
                    .toLowerCase()
                    .includes(search.toLowerCase())
            ) {
                return false
            }
            if (filter === 'uncounted' && l.counted_qty != null) return false
            if (filter === 'counted' && l.counted_qty == null) return false
            if (filter === 'activity' && !l.has_activity_today) return false
            if (filter === 'no_activity' && l.has_activity_today) return false
            if (
                filter === 'variance' &&
                (l.counted_qty == null || l.variance === 0)
            ) {
                return false
            }
            return true
        })
    }, [lines, filter, search])

    const countedLines = useMemo(
        () => lines.filter((l) => l.counted_qty != null).length,
        [lines],
    )

    if (isLoading) {
        return <div className="p-6">{m.common_loading()}</div>
    }

    if (!session) {
        return (
            <div className="p-6 text-muted-foreground">
                {m.stocktake_session_not_found()}
            </div>
        )
    }

    const editable =
        session.status === 'draft' ||
        session.status === 'counting' ||
        session.status === 'pending_approval'
    // WARNING_THRESHOLD phải khớp với constant trong stocktake-line-row.
    const WARNING_THRESHOLD = 0.1
    const hasWarningWithoutNote = lines.some((l) => {
        if (l.counted_qty == null || l.variance === 0) return false
        const ratio =
            l.expected_qty > 0 ? Math.abs(l.variance) / l.expected_qty : 0
        const isWarning = ratio > WARNING_THRESHOLD
        return isWarning && !(l.line_notes ?? '').trim()
    })
    // Không còn yêu cầu đếm hết dòng — dòng chưa gõ = "không lệch". Chỉ chặn
    // khi có dòng lệch nhiều (> WARNING_THRESHOLD) mà user quên điền lý do.
    const canSubmit =
        (session.status === 'draft' || session.status === 'counting') &&
        !hasWarningWithoutNote
    const canApprove =
        session.status === 'pending_approval' && !hasWarningWithoutNote
    const canCancel =
        session.status === 'draft' ||
        session.status === 'counting' ||
        session.status === 'pending_approval'

    return (
        <div className="space-y-4 page-pad">
            {/* Sticky header */}
            <Card className="sticky top-0 z-10 bg-card shadow-sm">
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <PageBackButton
                                onBack={() =>
                                    void navigate({ to: '/stocktake' })
                                }
                            />
                            <ClipboardList className="size-5 shrink-0" />
                            <CardTitle className="font-mono text-base md:text-lg break-all">
                                {session.code}
                            </CardTitle>
                            <Badge variant="outline">
                                {KIND_LABELS[session.kind]()}
                            </Badge>
                            <Badge
                                variant="outline"
                                className={
                                    session.session_time === 'morning'
                                        ? 'border-emerald-200 text-emerald-700 bg-emerald-50'
                                        : 'border-blue-200 text-blue-700 bg-blue-50'
                                }
                            >
                                {session.session_time === 'morning'
                                    ? m.stocktake_session_time_morning()
                                    : m.stocktake_session_time_evening()}
                            </Badge>
                            <Badge className={STATUS_COLORS[session.status]}>
                                {STATUS_LABELS[session.status]()}
                            </Badge>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {canSubmit && (
                                <Button
                                    className="h-11 md:h-9"
                                    onClick={() => setSubmitOpen(true)}
                                >
                                    <Send className="size-4 mr-1" />
                                    {m.stocktake_action_submit()}
                                </Button>
                            )}
                            {canApprove && (
                                <Button
                                    className="h-11 md:h-9"
                                    onClick={() => setApproveOpen(true)}
                                >
                                    {m.stocktake_action_approve()}
                                </Button>
                            )}
                            {canCancel && (
                                <Button
                                    className="h-11 md:h-9"
                                    variant="outline"
                                    onClick={async () => {
                                        cancelWorkflow.step('warning_shown')
                                        if (
                                            !confirm(
                                                m.stocktake_action_cancel() +
                                                    '?',
                                            )
                                        ) {
                                            cancelWorkflow.finish('closed')
                                            return
                                        }
                                        cancelWorkflow.step('warning_confirmed')
                                        try {
                                            await cancelWorkflow.run(() =>
                                                cancel.mutateAsync(id),
                                            )
                                            toast.success(
                                                m.stocktake_cancel_success(),
                                            )
                                            void navigate({ to: '/stocktake' })
                                        } catch {
                                            // The mutation hook owns the API error toast.
                                        }
                                    }}
                                >
                                    <Trash2 className="size-4 mr-1" />
                                    {m.stocktake_action_cancel()}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Progress */}
                    <p className="text-sm text-muted-foreground mt-2">
                        {m.stocktake_progress({
                            counted: String(countedLines),
                            total: String(session.total_lines),
                        })}
                    </p>
                    {/* Reserve height cả khi không có warning để layout không
                     * xô lệch khi user gõ vào rồi bỏ trống — dùng visibility
                     * hidden thay vì unmount. */}
                    <p
                        className={cn(
                            'mt-1 text-sm text-amber-700',
                            !hasWarningWithoutNote && 'invisible',
                        )}
                    >
                        {m.stocktake_variance_note_required()}
                    </p>
                </CardHeader>

                <CardContent className="pt-0 pb-3 space-y-2">
                    {/* Filter chips — horizontal scroll mobile, wrap desktop */}
                    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 md:mx-0 md:flex-wrap md:overflow-visible md:px-0 md:pb-0">
                        {(
                            [
                                'all',
                                'uncounted',
                                'counted',
                                'variance',
                                'activity',
                                'no_activity',
                            ] as LineFilter[]
                        ).map((f) => (
                            <Button
                                key={f}
                                variant={filter === f ? 'default' : 'outline'}
                                size="sm"
                                className="shrink-0"
                                onClick={() => setFilter(f)}
                            >
                                {f === 'all'
                                    ? m.stocktake_filter_all()
                                    : f === 'uncounted'
                                      ? m.stocktake_filter_uncounted()
                                      : f === 'counted'
                                        ? m.stocktake_filter_counted()
                                        : f === 'variance'
                                          ? m.stocktake_filter_variance()
                                          : f === 'activity'
                                            ? m.stocktake_filter_activity()
                                            : m.stocktake_filter_no_activity()}
                            </Button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={m.stocktake_search_placeholder()}
                            className="h-11 md:h-9 md:max-w-xs"
                        />
                        <ScanLotCodeButton
                            parseLotId={parseAnyLotIdFromQR}
                            label={m.stocktake_scan_lot()}
                            onScanned={(lotId) => {
                                const line = lines.find(
                                    (l) => l.lot_id === lotId,
                                )
                                if (!line) {
                                    toast.error(
                                        m.stocktake_scan_lot_not_in_session(),
                                    )
                                    return
                                }
                                // Reset filter + search để row match luôn hiển
                                // thị bất kể filter chip nào đang active.
                                setFilter('all')
                                setSearch('')
                                // Chờ 1 frame cho React re-render row rồi mới
                                // scrollIntoView + focus input counted_qty.
                                requestAnimationFrame(() => {
                                    const row =
                                        document.querySelector<HTMLElement>(
                                            `[data-lot-row-id="${line.id}"]`,
                                        )
                                    if (!row) return
                                    row.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'center',
                                    })
                                    const input =
                                        row.querySelector<HTMLInputElement>(
                                            'input[type="number"]',
                                        )
                                    // Highlight row 2.5s + focus input. CSS
                                    // class `stocktake-scan-highlight` được
                                    // define trong styles.css bên dưới.
                                    row.classList.add(
                                        'stocktake-scan-highlight',
                                    )
                                    setTimeout(() => {
                                        row.classList.remove(
                                            'stocktake-scan-highlight',
                                        )
                                    }, 2500)
                                    input?.focus()
                                    input?.select()
                                })
                            }}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Bảng kiểm — card list mobile, table desktop */}
            <div className="space-y-2 md:hidden">
                {filteredLines.map((line, i) => (
                    <StocktakeLineCard
                        key={line.id}
                        line={line}
                        index={i}
                        editable={editable}
                        onChange={(data, workflow) => {
                            void workflow
                                .run(
                                    () =>
                                        updateLine.mutateAsync({
                                            lineId: line.id,
                                            data,
                                        }),
                                    (result) => result.data?.id,
                                )
                                .catch(() => {})
                        }}
                    />
                ))}
            </div>
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>
                                {m.stocktake_line_col_lot_code()}
                            </TableHead>
                            <TableHead>
                                {m.stocktake_line_col_warehouse()}
                            </TableHead>
                            <TableHead>
                                {m.stocktake_line_col_material()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.stocktake_line_col_system_qty()}
                            </TableHead>
                            <TableHead>
                                {m.stocktake_line_col_counted()}
                            </TableHead>
                            <TableHead className="w-40 text-right">
                                {m.stocktake_line_col_variance()}
                            </TableHead>
                            <TableHead className="w-56">
                                {m.stocktake_line_col_notes()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredLines.map((line, i) => (
                            <StocktakeLineRow
                                key={line.id}
                                line={line}
                                index={i}
                                editable={editable}
                                onChange={(data, workflow) => {
                                    void workflow
                                        .run(
                                            () =>
                                                updateLine.mutateAsync({
                                                    lineId: line.id,
                                                    data,
                                                }),
                                            (result) => result.data?.id,
                                        )
                                        .catch(() => {})
                                }}
                            />
                        ))}
                    </TableBody>
                </Table>
            </div>

            <StocktakeSubmitDialog
                open={submitOpen}
                onOpenChange={setSubmitOpen}
                sessionId={id}
                counted={countedLines}
                total={session.total_lines}
                variance={session.variance_lines}
            />
            <StocktakeApproveDialog
                open={approveOpen}
                onOpenChange={setApproveOpen}
                sessionId={id}
                variance={session.variance_lines}
                sessionCode={session.code}
            />
        </div>
    )
}
